import {
  growintel_logo_white,
  hero_image,
  powered_by_stripe
} from '@growdash/design-system/assets';
import { Button, Label, Typography } from '@growdash/design-system/atoms';
import {
  RHFInput,
  RHFSelect
} from '@growdash/design-system/molecules/index.js';
import { yupResolver } from '@hookform/resolvers/yup';
import { CardElement } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { useId, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { PlanDto } from '../../../generated';
import { SignUpCountry } from '../auth/sign-up';

// Helper function to get date one month from today
const getOneMonthFromToday = (): string => {
  const today = new Date();
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const day = oneMonthLater.getDate();
  const month = months[oneMonthLater.getMonth()];
  const year = oneMonthLater.getFullYear();

  // Add ordinal suffix to day
  const getOrdinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

interface BillingDetailsStepProps {
  selectedTier: PlanDto;
  onSubmit: (billingDetails: BillingFormValues) => void | Promise<void>;
  isYearly: boolean;
  billingError: string | null;
  countries: SignUpCountry[];
  userCountry: string;
  logout: () => void;
}

export type BillingFormValues = Yup.InferType<typeof BillingSchema>;

const BillingSchema = Yup.object().shape({
  cardName: Yup.string().required('Card Name is Required'),
  country: Yup.string().required('Country is Required'),
  annualPlan: Yup.boolean().required('Annual plan is Required'),
  address: Yup.string().required('Billing Address is Required')
});

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

export default function BillingDetailsStep({
  selectedTier,
  onSubmit,
  isYearly,
  billingError,
  userCountry,
  countries,
  logout
}: BillingDetailsStepProps) {
  const [selectedPlanInfo] = useState<PlanDto>(selectedTier);
  const [cardComplete, setCardComplete] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const methods = useForm({
    resolver: yupResolver(BillingSchema),
    defaultValues: {
      cardName: '',
      country: '',
      annualPlan: isYearly,
      address: ''
    },
    mode: 'onBlur'
  });

  const { handleSubmit } = methods;

  const id = useId();

  const onFormSubmit: SubmitHandler<BillingFormValues> = data => {
    if (!cardComplete) {
      return;
    }
    onSubmit(data);
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete);
  };

  return (
    <>
      <div>
        <div className='h-screen lg:grid lg:grid-cols-5 bg-white force-light-mode'>
          {/* Left side - Background image */}
          <div className='relative hidden lg:flex lg:col-span-2 h-full p-7'>
            <div
              className='w-full h-full overflow-hidden flex flex-col justify-between rounded-lg bg-cover bg-center bg-no-repeat p-10'
              style={{
                backgroundImage: `url(${hero_image})`
              }}
            >
              <img
                src={growintel_logo_white}
                alt='growintel'
                className='max-w-[250px]'
              />
              <div className='flex flex-col gap-2'>
                <h1 className='font-normal text-[50px] leading-[120%] tracking-[-0.01em] align-middle text-white'>
                  You&apos;re one step away from unlocking Growintel.
                </h1>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className='flex flex-col justify-center p-6 md:px-8 md:py-8 lg:px-16 lg:col-span-3'>
            <div className='flex justify-end pb-5 border-b border-gray-200 w-full mb-9'>
              <button
                type='button'
                onClick={logout}
                className='text-red-500 text-sm font-medium'
              >
                Log out
              </button>
            </div>

            <div className='mx-auto w-full sm:pb-9 '>
              {/* Step indicator */}
              <div className='mb-6'>
                <p className='w-fit rounded-full py-1 px-2 bg-[#F5F5F6] text-[12px] text-[#72778A] mb-2 border border-[#EAEBEF]'>
                  Step 3
                </p>
                <h1 className='max-sm:leading-[1.25em] text-[1.5rem] sm:text-[2rem] font-bold text-black mb-2'>
                  You&apos;re one step away from unlocking 30 days of Growintel{' '}
                  {userCountry ? ` ${userCountry}` : ''} for free.
                </h1>
                <p className='text-black text-[0.857rem] sm:text-[1.5rem] font-bold'>
                  🔐 Secure & encrypted. Cancel anytime with one click
                </p>
              </div>

              {/* Form */}
              <div className='space-y-4'>
                <FormProvider {...methods}>
                  <form
                    onSubmit={handleSubmit(onFormSubmit)}
                    className='space-y-4'
                  >
                    <div className='space-y-2'>
                      <Label
                        className='text-black'
                        htmlFor={`card-element-${id}`}
                      >
                        Card Details
                      </Label>
                      <div
                        className={`border h-10 px-3 py-2 rounded-md ${
                          billingError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <CardElement
                          id={`card-element-${id}`}
                          options={CARD_ELEMENT_OPTIONS}
                          onChange={handleCardChange}
                        />
                      </div>
                      {billingError && (
                        <Typography variant='body2' className='text-red-500'>
                          {billingError}
                        </Typography>
                      )}
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label className='text-black' htmlFor='cardName'>
                          Card Name
                        </Label>
                        <RHFInput
                          name='cardName'
                          id='cardName'
                          placeholder='Card Name'
                          className='text-black'
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label className='text-black' htmlFor='country'>
                          Country
                        </Label>
                        <RHFSelect
                          name='country'
                          disabled={false}
                          className='text-black'
                          options={countries}
                        />
                      </div>
                    </div>
                    <div className='space-y-2'>
                      <Label className='text-black' htmlFor='address'>
                        Billing Address
                      </Label>
                      <RHFInput
                        name='address'
                        id='address'
                        placeholder='Enter your billing address'
                        className='text-black'
                      />
                    </div>
                    {/* <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label className='text-black' htmlFor='country'>
                          Country
                        </Label>
                        <RHFSelect
                          name='country'
                          disabled={false}
                          className='text-black'
                          options={countries}
                        />
                      </div>
                    </div> */}

                    {/* Pricing Information */}
                    {/* <div className='mt-4'>
                      <Typography
                        variant='h4'
                        className='text-[24px] text-[#6A66B6]'
                      >
                        {selectedPlanInfo.currency}{' '}
                        {annualPlan
                          ? (selectedPlanInfo.yearlyPrice / 100).toFixed(2)
                          : (selectedPlanInfo.monthlyPrice / 100).toFixed(
                              2
                            )}{' '}
                        / {annualPlan ? 'year' : 'month'}
                      </Typography>
                      <ButtonGroup className='mt-4'>
                        <Button
                          type='button'
                          className={
                            !annualPlan
                              ? 'bg-[#F5F5F6] text-[#494B54] border border-[#72778A]'
                              : 'bg-white text-[#72778A] border border-[#EAEBEF]'
                          }
                          onClick={() => {
                            setAnnualPlan(false);
                            methods.setValue('annualPlan', false);
                          }}
                        >
                          Monthly Billing
                        </Button>
                        <Button
                          type='button'
                          className={
                            annualPlan
                              ? 'bg-[#F5F5F6] text-[#494B54] border border-[#72778A]'
                              : 'bg-white text-[#72778A] border border-[#EAEBEF]'
                          }
                          onClick={() => {
                            setAnnualPlan(true);
                            methods.setValue('annualPlan', true);
                          }}
                        >
                          Annual Billing
                        </Button>
                      </ButtonGroup>
                    </div> */}

                    {/* Terms and Conditions */}
                    <div className='flex items-start space-x-3 mt-6'>
                      <input
                        type='checkbox'
                        checked={termsChecked}
                        onChange={() => setTermsChecked(!termsChecked)}
                        id='terms'
                        className='mt-1 h-4 w-4 text-[#060081] border-gray-300 rounded focus:ring-[#060081]'
                        required
                      />
                      <label htmlFor='terms' className='text-sm text-gray-600'>
                        By checking this option, I accept the{' '}
                        <a
                          href='https://mygrowdash.com/page/terms-and-conditions'
                          className='text-[#060081] hover:underline'
                          target='_blank'
                          rel='noreferrer'
                        >
                          Terms and Conditions
                        </a>{' '}
                        of the service and acknowledge that I have read and
                        understood the{' '}
                        <a
                          href='https://mygrowdash.com/page/privacy-policy'
                          target='_blank'
                          className='text-[#060081] hover:underline'
                          rel='noreferrer'
                        >
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    <Button
                      className='w-full mt-6 bg-[#060081] text-white hover:bg-[#060081]/80 py-3 text-[14px] font-medium'
                      type='submit'
                      disabled={!cardComplete || !termsChecked}
                    >
                      Start my free trial
                    </Button>

                    {/* Trial information */}
                    <div className='flex justify-between items-center mt-4'>
                      <p className='text-gray-500 text-sm gap-2'>
                        30 days free trial,
                        {(selectedPlanInfo.monthlyPrice / 100).toFixed(2)}{' '}
                        {selectedPlanInfo.currency} after. Cancel anytime.{' '}
                        <br /> You won&apos;t be charged until{' '}
                        <span className='w-fit rounded-full px-1 bg-[#F5F5F6] text-[12px] text-[#72778A] border border-[#EAEBEF]'>
                          {getOneMonthFromToday()}
                        </span>
                      </p>
                      <div className='flex items-center space-x-2'>
                        <img
                          src={powered_by_stripe}
                          alt='stripe'
                          className='max-w-[150px] h-auto'
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className='container relative hidden h-lvh bg-white flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0'>
        <div className='lg:p-8 bg-white'>
          <div>
            {plansLength > 1 && (
              <Button onClick={goBack} variant='ghost'>
                <ArrowLeftIcon className='w-4 h-4' /> Go back
              </Button>
            )}
            <div className='flex items-center justify-between'>
              <Typography
                variant='h4'
                className='mb-1 text-[28px] text-[#191420]'
              >
                Add Billing Details for{' '}
                <span className='text-[#060081]'>{selectedPlanInfo.name}</span>
                {(selectedPlanInfo.featureSet?.length ?? 0) > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant='ghost' size='icon' className='ml-2'>
                          <InfoIcon
                            width={28}
                            height={28}
                            className='text-[#060081] text-muted-foreground'
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {selectedPlanInfo.featureSet?.map(feature => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Typography>
            </div>
            <Typography
              variant='body2'
              className='mb-6 text-[15px] text-[#72778A]'
            >
              {selectedPlanInfo.description}
            </Typography>
          </div>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)} className='space-y-4'>
              <Typography className='text-black dark:text-black' variant='h4'>
                Payment Details
              </Typography>

              <div className='space-y-2'>
                <Label className='text-black' htmlFor='cardName'>
                  Card Name
                </Label>
                <RHFInput
                  name='cardName'
                  id='cardName'
                  placeholder='Card Name'
                  className='text-black'
                />
              </div>

              <div className='space-y-2'>
                <Label className='text-black' htmlFor={`card-element-${id}`}>
                  Card Details
                </Label>
                <div
                  className={`border h-10 px-3 py-2 rounded-md ${
                    billingError ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <CardElement
                    id={`card-element-${id}`}
                    options={CARD_ELEMENT_OPTIONS}
                    onChange={handleCardChange}
                  />
                </div>
                {billingError && (
                  <Typography variant='body2' className='text-red-500'>
                    {billingError}
                  </Typography>
                )}
              </div>

              <div className='space-y-2'>
                <Label className='text-black' htmlFor='address'>
                  Billing Address
                </Label>
                <RHFInput
                  name='address'
                  id='address'
                  placeholder='Enter your billing address'
                  className='text-black'
                />
              </div>

             
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label className='text-black' htmlFor='country'>
                    Country
                  </Label>
                  <RHFSelect
                    name='country'
                    disabled={false}
                    className='text-black'
                    options={countries}
                  />
                </div>
              </div>

          
              <div className='mt-4'>
                <Typography
                  variant='h4'
                  className='mb-1 text-[28px] text-[#191420] dark:text-[#191420]'
                >
                  Payment Details
                </Typography>
                <Typography variant='h4' className='text-[24px] text-[#6A66B6]'>
                  {selectedPlanInfo.currency}{' '}
                  {annualPlan
                    ? (selectedPlanInfo.yearlyPrice / 100).toFixed(2)
                    : (selectedPlanInfo.monthlyPrice / 100).toFixed(2)}{' '}
                  / {annualPlan ? 'year' : 'month'}
                </Typography>
                <ButtonGroup className='mt-4'>
                  <Button
                    type='button'
                    className={
                      !annualPlan
                        ? 'bg-[#F5F5F6] text-[#494B54] border border-[#72778A]'
                        : 'bg-white text-[#72778A] border border-[#EAEBEF]'
                    }
                    onClick={() => {
                      setAnnualPlan(false);
                      methods.setValue('annualPlan', false);
                    }}
                  >
                    Monthly Billing
                  </Button>
                  <Button
                    type='button'
                    className={
                      annualPlan
                        ? 'bg-[#F5F5F6] text-[#494B54] border border-[#72778A]'
                        : 'bg-white text-[#72778A] border border-[#EAEBEF]'
                    }
                    onClick={() => {
                      setAnnualPlan(true);
                      methods.setValue('annualPlan', true);
                    }}
                  >
                    Annual Billing
                  </Button>
                </ButtonGroup>
              </div>

              <Button
                className='w-full mt-4 bg-[#060081] text-white hover:bg-[#060081]/80'
                type='submit'
                disabled={!cardComplete}
              >
                Complete Subscription
              </Button>
            </form>
          </FormProvider>

          <Button onClick={goProfile} variant='ghost' className='mt-4'>
            Go Back to Profile
          </Button>
        </div>
        <div className='relative hidden h-full flex-col bg-gradient-to-r from-[#5BE0DC] to-[#060081] p-10 text-foreground lg:flex'>
          <img
            src={sign_up_3}
            alt='step1'
            className='absolute bottom-0 right-0'
          />
        </div>
      </div> */}
    </>
  );
}
